/**
 * Authentication and Authorization Service
 * Provides secure role-based access control
 */

export enum UserRole {
  USER = 'user',
  CREATOR = 'creator',
  MODERATOR = 'moderator',
  ADMIN = 'admin'
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  permissions: string[];
  verified: boolean;
  created_at: string;
}

export interface Permission {
  CREATE_PICK: 'create_pick';
  MODERATE_CONTENT: 'moderate_content';
  ACCESS_ADMIN_PANEL: 'access_admin_panel';
  MANAGE_USERS: 'manage_users';
  VIEW_ANALYTICS: 'view_analytics';
  MANAGE_SUBSCRIPTIONS: 'manage_subscriptions';
}

/**
 * Default permissions for each role
 */
const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  [UserRole.USER]: [],
  [UserRole.CREATOR]: ['create_pick', 'view_analytics'],
  [UserRole.MODERATOR]: ['moderate_content', 'view_analytics'],
  [UserRole.ADMIN]: [
    'create_pick',
    'moderate_content',
    'access_admin_panel',
    'manage_users',
    'view_analytics',
    'manage_subscriptions'
  ]
};

/**
 * Get current authenticated user from localStorage
 */
export function getCurrentUser(): AuthUser | null {
  try {
    const userData = localStorage.getItem('user');
    if (!userData) return null;

    const user = JSON.parse(userData);

    // Ensure user has proper role and permissions
    const role = user.role as UserRole || UserRole.USER;
    const permissions = user.permissions || ROLE_PERMISSIONS[role];

    return {
      ...user,
      role,
      permissions,
      verified: user.verified || false
    };
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
}

/**
 * Check if current user has a specific permission
 */
export function hasPermission(permission: string): boolean {
  const user = getCurrentUser();
  if (!user) return false;

  return user.permissions.includes(permission);
}

/**
 * Check if current user has a specific role or higher
 */
export function hasRole(requiredRole: UserRole): boolean {
  const user = getCurrentUser();
  if (!user) return false;

  const roleHierarchy = [UserRole.USER, UserRole.CREATOR, UserRole.MODERATOR, UserRole.ADMIN];
  const userRoleIndex = roleHierarchy.indexOf(user.role);
  const requiredRoleIndex = roleHierarchy.indexOf(requiredRole);

  return userRoleIndex >= requiredRoleIndex;
}

/**
 * Check if current user is admin
 */
export function isAdmin(): boolean {
  return hasRole(UserRole.ADMIN);
}

/**
 * Check if current user is creator or higher
 */
export function isCreator(): boolean {
  return hasRole(UserRole.CREATOR);
}

/**
 * Check if current user is moderator or higher
 */
export function isModerator(): boolean {
  return hasRole(UserRole.MODERATOR);
}

/**
 * Validate user session and refresh permissions
 */
export async function validateSession(): Promise<boolean> {
  const user = getCurrentUser();
  if (!user) return false;

  try {
    // TODO: Implement proper session validation with backend
    // For now, just check if user data exists and is valid
    return user.id && user.email && user.role ? true : false;
  } catch (error) {
    console.error('Session validation failed:', error);
    return false;
  }
}

/**
 * Logout current user
 */
export function logout(): void {
  localStorage.removeItem('user');
  window.dispatchEvent(new Event('userStateChanged'));
}

/**
 * Update user permissions (admin only)
 */
export function updateUserPermissions(userId: string, permissions: string[]): boolean {
  if (!hasPermission('manage_users')) {
    throw new Error('Insufficient permissions to manage users');
  }

  // TODO: Implement backend API call to update permissions
  console.log(`Updating permissions for user ${userId}:`, permissions);
  return true;
}

/**
 * Get display name for role
 */
export function getRoleDisplayName(role: UserRole): string {
  switch (role) {
    case UserRole.USER:
      return 'User';
    case UserRole.CREATOR:
      return 'Creator';
    case UserRole.MODERATOR:
      return 'Moderator';
    case UserRole.ADMIN:
      return 'Administrator';
    default:
      return 'Unknown';
  }
}